import { useEffect, useRef, useState } from 'react';
import Accordion from 'src/components/atoms/Accordion';
import { useLabels } from 'src/StoreDataProvider';
import { FooterProps } from './footer.types';
import { useLocation } from 'react-router-dom';

const Footer: (props: FooterProps) => JSX.Element = props => {
  const labels = useLabels();

  const [footerServiceItems] = useState(new Map<string, string>());
  const [footerServiceItemArray, setFooterServiceItemArray] = useState(new Array<string[]>());

  const footerContainerRef = useRef<HTMLElement>(null);
  const rootContainer = document.getElementById('root');
  const allContainer = document.getElementById('all');
  const location = useLocation();

  useEffect(() => {
    footerServiceItems.clear();
    let count = 1;
    while (labels[`footerServiceTitle${count}`]) {
      const footerServiceLinksItem = labels[`footerServiceLink${count}`];
      if (footerServiceLinksItem) {
        footerServiceItems.set(labels[`footerServiceTitle${count}`], footerServiceLinksItem);
        // }
      }
      count++;
    }
    setFooterServiceItemArray(Array.from(footerServiceItems.entries()));
  }, [labels]);

  // Adjust footer
  useEffect(() => {
    if (!rootContainer || !allContainer) return;
    const resizeObserver = new ResizeObserver(() => {
      if (footerContainerRef.current) {
        // Check footer end position
        const footerEnd = footerContainerRef.current?.offsetTop + footerContainerRef.current?.clientHeight;
        // Check space under footer
        const spaceUnderFooter = window.innerHeight - footerEnd;
        const newDisplay = spaceUnderFooter > 0 ? 'flex' : 'block';
        // If there is space under the footer
        // and the display is not correct
        // adjust the display
        // and the height of the container
        if (spaceUnderFooter !== 0 && allContainer.style.display !== newDisplay) {
          allContainer.style.display = newDisplay;
          allContainer.style.height = spaceUnderFooter > 0 ? '100vh' : 'fit-content';
        }
      }
    });
    resizeObserver.observe(rootContainer);
    return () => resizeObserver.disconnect();
  }, [allContainer, location]);

  return (<footer className="footer-container" ref={footerContainerRef}>
      <div className="footer-section footer-section__print__link">
        <div className="print__link">
          <button
            className="print__link__item colored__link"
            onClick={() => window.print()}
          >
            <em className="icon-drucker" aria-hidden="true" />
            <div className="icon__text">{labels.footerPrint}</div>
          </button>
        </div>
      </div>
      <div className="footer-stripe"></div>
      <div className="footer-section">
        <div className="footer__contact-links-section">
          <div className="footer__contact-links-section-services">
            <div className="footer__contact-links-section-services__desktop">
              <div className="footer__heading">{labels.footerServices}:</div>
              <ul>
                {footerServiceItemArray.map(entry => (<li
                    className="footer__contact-links-link"
                    key={`footer-service-item-${entry[0]}`}
                  >
                    <a
                      className="colored__link"
                      href={entry[1]}
                      aria-label={entry[0]}
                    >
                      <em className="icon-pfeil-rechts" aria-hidden="true" />
                      <span className="icon__text">{entry[0]}</span>
                    </a>
                  </li>))}
              </ul>
            </div>
            <Accordion
              className="footer__contact-links-section-services__mobile"
              title={labels.footerServices}
              isActive={false}
              hierachy={3}
            >
              <ul>
                {footerServiceItemArray.map(entry => (<li
                    className="footer__contact-links-link"
                    key={`footer-service-item-${entry[0]}`}
                  >
                    <a
                      className="colored__link"
                      href={entry[1]}
                      aria-label={entry[0]}
                    >
                      <em className="icon-pfeil-rechts" aria-hidden="true" />
                      <span className="icon__text">{entry[0]}</span>
                    </a>
                  </li>))}
              </ul>
            </Accordion>
          </div>
          <div className="footer__contact-links-section-services">
            <div className="footer__heading">{labels.footerFollow}:</div>
            <ul>
              <li className="footer__contact-links-link">
                <a
                  href={labels.footerFollowLinkTwitter}
                  className="colored__link"
                  target="_blank"
                  rel="noreferrer"
                >
                  <em
                    className="icon-social-media-twitter"
                    aria-hidden="true"
                  />
                  <span className="icon__text">Twitter</span>
                </a>
              </li>
              <li className="footer__contact-links-link">
                <a
                  href={labels.footerFollowLinkYoutube}
                  className="colored__link"
                  target="_blank"
                  rel="noreferrer"
                >
                  <em
                    className="icon-social-media-youtube"
                    aria-hidden="true"
                  />
                  <span className="icon__text">Youtube</span>
                </a>
              </li>
              <li className="footer__contact-links-link">
                <a
                  href={labels.footerFollowLinkXING}
                  className="colored__link"
                  target="_blank"
                  rel="noreferrer"
                >
                  <em className="icon-social-media-xing" aria-hidden="true" />
                  <span className="icon__text">XING</span>
                </a>
              </li>
              <li className="footer__contact-links-link">
                <a
                  href={labels.footerFollowLinkLinkedIn}
                  className="colored__link"
                  target="_blank"
                  rel="noreferrer"
                >
                  <em
                    className="icon-social-media-linkedin"
                    aria-hidden="true"
                  />
                  <span className="icon__text">LinkedIn</span>
                </a>
              </li>
              <li className="footer__contact-links-link">
                <a
                  href={labels.footerFollowLinkInstagram}
                  className="colored__link"
                  target="_blank"
                  rel="noreferrer"
                >
                  <em
                    className="icon-social-media-instagram"
                    aria-hidden="true"
                  />
                  <span className="icon__text">Instagram</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="footer-section" aria-label="Hinweise">
        <div className="footer__about-section">
          <div className="dark__blue footer__about-link">
            {new Date().getFullYear()} {labels.footerCopyright}
          </div>
          <ul className="footer__link-item-wrapper">
            {/* <li className="footer__about-link">
              <Link
                className="dark__blue"
                to={`/${path}/sitemap`}
                aria-label={labels.footerSitemap}
              >
                <span className="link__label">{labels.footerSitemap}</span>
              </Link>
            </li> */}
            <li className="footer__about-link">
              <a
                className="dark__blue"
                href={labels.footerAccessibilityLink}
                aria-label={labels.footerAccessibility}
              >
                <span className="link__name-text">
                  {labels.footerAccessibility}
                </span>
              </a>
            </li>
            <li className="footer__about-link">
              <a
                className="dark__blue"
                href={labels.footerDataProtectionLink}
                aria-label={labels.footerDataProtection}
              >
                <span className="link__name-text">
                  {labels.footerDataProtection}
                </span>
              </a>
            </li>
            <li className="footer__about-link">
              <a
                className="dark__blue"
                href={labels.footerImprintLink}
                aria-label={labels.footerImprint}
              >
                <span className="link__name-text">{labels.footerImprint}</span>
              </a>
            </li>
          </ul>
          <button
            className="footer__to-top button-nav-outline"
            onClick={() => {
              if (document.getElementById('main-anchor')) {
                document.getElementById('main-anchor')?.focus();
              }
              window.scrollTo({
                top: 0, behavior: 'smooth'
              });
            }}
          >
            <em className="icon-pfeil-hoch" aria-hidden="true" />
            <span className="icon__text-no-padding-right">
              {labels.footerBackToTop}
            </span>
          </button>
        </div>
      </div>
    </footer>);
};

export default Footer;
